// Import React and hooks
import React from 'react';
import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
  Chip,
  InputAdornment,
  IconButton,
  Tooltip,
  FormHelperText
} from '@mui/material';
import {
  GitHub as GitHubIcon,
  Storage as GitLabIcon,
  Code as BitbucketIcon,
  Code as CodeIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  InfoOutlined as InfoIcon
} from '@mui/icons-material';
import Select from 'react-select';
import useGitRepository from '../../hooks/useGitRepository';
import useNotification from '../../hooks/useNotification';

const repositoryProviders = [
  { value: 'github', label: 'GitHub', icon: <GitHubIcon /> },
  { value: 'gitlab', label: 'GitLab', icon: <GitLabIcon />, allowCustomUrl: true },
  { value: 'bitbucket', label: 'Bitbucket', icon: <BitbucketIcon /> },
  { value: 'custom', label: 'Custom Git Server', icon: <CodeIcon /> }
];

const authMethods = [
  { value: 'https', label: 'HTTPS' },
  { value: 'ssh', label: 'SSH' },
  { value: 'pat', label: 'Personal Access Token' },
  { value: 'oauth', label: 'OAuth' }
];

/**
 * Git Repository Form
 * Component for connecting to a Git repository
 * 
 * @param {Object} props - Component props
 * @param {string} props.projectId - Project ID
 * @param {Function} props.onSuccess - Callback function on successful connection
 * @param {Function} props.onCancel - Callback function on form cancellation
 * @returns {React.ReactElement} Component
 */
const GitRepositoryForm = ({ projectId, onSuccess, onCancel, containerHeight }) => {
  // Default container height if not provided
  const effectiveContainerHeight = containerHeight || 'calc(100vh - 180px)';
  const { 
    connectRepository, 
    getRepositoryInfo, 
    disconnectRepository, 
    loading, 
    error 
  } = useGitRepository();
  const { showNotification } = useNotification();
  
  // Form state
  const [provider, setProvider] = useState('github');
  const [repoUrl, setRepoUrl] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');
  const [branch, setBranch] = useState('main');
  const [authMethod, setAuthMethod] = useState('pat'); // Default to PAT for GitLab
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  // UI state
  const [showPassword, setShowPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [connectedRepo, setConnectedRepo] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false); // Flag to prevent multiple submissions
  
  // Refs for preventing focus loss
  const repoUrlInputRef = useRef(null);
  const usernameInputRef = useRef(null);
  const passwordInputRef = useRef(null);
  const tokenInputRef = useRef(null);
  const branchInputRef = useRef(null);
  
  // Check if repository is already connected - only check sessionStorage, never make API calls on mount
  useEffect(() => {
    // Track if this component is still mounted to prevent state updates after unmount
    let isMounted = true;
    
    if (projectId && isMounted) {
      // ONLY check sessionStorage, never make API calls on component mount
      // First, check for an existing connection
      const storedConnection = sessionStorage.getItem(`git_repo_connection_${projectId}`);
      if (storedConnection) {
        try {
          const parsedConnection = JSON.parse(storedConnection);
          if (parsedConnection && parsedConnection.url) {
            console.log('Using stored connection info from sessionStorage');
            setConnectedRepo(parsedConnection);
          }
        } catch (err) {
          console.error('Error parsing stored connection:', err);
          // Clear invalid storage
          sessionStorage.removeItem(`git_repo_connection_${projectId}`);
        }
      } else {
        // Next, check if we know there's no connection
        const cacheKey = `repo_check_${projectId}`;
        const hasChecked = sessionStorage.getItem(cacheKey);
        
        if (hasChecked === 'not_found') {
          console.log('Using cached "not connected" status from sessionStorage');
          setConnectedRepo(null);
        }
      }
    }
    
    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, [projectId]);
  
  // Helper function to validate the form
  const validateForm = () => {
    // Create new errors object
    const errors = {};
    
    // Only validate URL if it has changed
    if (!repoUrl.trim()) {
      errors.repoUrl = 'Repository URL is required';
    } else {
      // Basic URL validation
      try {
        const url = new URL(repoUrl);
        if (!url.protocol || !url.hostname) {
          errors.repoUrl = 'Invalid URL format';
        }
      } catch (e) {
        errors.repoUrl = 'Invalid URL format';
      }
    }
    
    // Only validate credentials if auth method requires them
    if (authMethod === 'https' || authMethod === 'basic') {
      if (username.trim() === '') {
        errors.username = 'Username is required for HTTPS authentication';
      }
      if (password.trim() === '') {
        errors.password = 'Password is required for HTTPS authentication';
      }
    } else if (authMethod === 'pat' || authMethod === 'oauth') {
      if (token.trim() === '') {
        errors.token = 'Token is required for this authentication method';
      }
    }
    
    // Branch is only validated when advanced options are shown
    if (showAdvanced && !branch.trim()) {
      errors.branch = 'Branch name is required';
    }
    
    // Only update state if errors have changed
    const errorKeys = Object.keys(errors);
    const oldErrorKeys = Object.keys(validationErrors);
    
    if (errorKeys.length !== oldErrorKeys.length || 
        errorKeys.some(key => errors[key] !== validationErrors[key])) {
      setValidationErrors(errors);
    }
    
    return errorKeys.length === 0;
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm() || isSubmitting) {
      return;
    }
    
    // Set submitting flag to prevent multiple submissions
    setIsSubmitting(true);
    
    // Clear the cache for this project to ensure we can get fresh data after connecting
    const cacheKey = `repo_check_${projectId}`;
    sessionStorage.removeItem(cacheKey);
    console.log(`Cleared repository check cache for project ${projectId}`);
    
    // Prepare data for repository connection
    const repositoryData = {
      url: repoUrl,
      provider: provider,
      auth_method: authMethod,
      branch: branch,
      create_workspace: true
    };
    
    // Add credentials based on auth method
    if (authMethod === 'https' || authMethod === 'basic') {
      repositoryData.credentials = { username, password };
    } else if (authMethod === 'pat' || authMethod === 'oauth') {
      // For GitLab PAT, we need to use the correct format
      if (provider === 'gitlab') {
        repositoryData.credentials = { token };
        repositoryData.token = token;
        // GitLab authentication will be handled on the server with oauth2: prefix
        console.log('Using GitLab authentication with token');
      } else {
        repositoryData.credentials = { token };
        repositoryData.token = token; // Add token directly as well for compatibility
      }
    }
    
    // Log the authentication approach for debugging
    console.log(`Using ${authMethod} authentication for ${provider} repository`);
    
    // Don't log sensitive data
    const logData = {
      ...repositoryData, 
      credentials: '***REDACTED***', 
      token: repositoryData.token ? '***REDACTED***' : undefined
    };
    console.log('Connecting repository with data:', logData);
    
    try {
      // Set loading state via the hook function
      const result = await connectRepository(projectId, repositoryData);
      
      console.log('Repository connection result:', result);
      
      if (result && result.success) {
        // If connected successfully, construct the repository info manually without an API call
        // This avoids the unnecessary API call to getRepositoryInfo right after connection
        const connectionData = {
          url: repositoryData.url,
          provider: repositoryData.provider,
          branch: repositoryData.branch,
          auth_method: repositoryData.auth_method,
          // Estimated default values for other fields
          file_count: 0, // This will be updated when users actually use Claude Code
          status: 'connected',
          connected_at: new Date().toISOString()
        };
        
        console.log('Repository connected with data:', connectionData);
        setConnectedRepo(connectionData);
        
        // Store connection data in sessionStorage to avoid unnecessary API calls
        sessionStorage.setItem(`git_repo_connection_${projectId}`, JSON.stringify(connectionData));
        // Also clear the "not found" marker if it was set previously
        sessionStorage.removeItem(`repo_check_${projectId}`);
        
        // Call success callback
        if (onSuccess) {
          onSuccess(connectionData);
        }
        
        // Show success notification
        showNotification({
          message: 'Repository connected successfully',
          type: 'success'
        });
        
        // Reset form state
        setIsSubmitting(false);
      } else {
        console.error('Repository connection failed:', result);
        showNotification({
          message: 'Failed to connect repository. Please check your credentials and try again.',
          type: 'error'
        });
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error('Error connecting repository:', err);
      showNotification({
        message: `Error connecting repository: ${err.response?.data?.message || err.message}`,
        type: 'error'
      });
      setIsSubmitting(false);
    }
  };
  
  // Handle disconnect repository
  const handleDisconnect = async () => {
    try {
      await disconnectRepository(projectId);
      setConnectedRepo(null);
      
      // Clear stored connection data from sessionStorage
      sessionStorage.removeItem(`git_repo_connection_${projectId}`);
      
      // Also clear the repo check cache
      sessionStorage.removeItem(`repo_check_${projectId}`);
      
      // Show success notification
      showNotification({
        message: 'Repository disconnected successfully',
        type: 'success'
      });
    } catch (err) {
      console.error('Error disconnecting repository:', err);
      showNotification({
        message: `Error disconnecting repository: ${err.message}`,
        type: 'error'
      });
    }
  };
  
  // Handle auth method change
  const handleAuthMethodChange = (e) => {
    let newAuthMethod;
    
    // Handle both React Select and regular events
    if (e && e.value) {
      // React Select format
      newAuthMethod = e.value;
    } else if (e && e.target && e.target.value) {
      // Standard DOM event format
      newAuthMethod = e.target.value;
    } else {
      console.error('Invalid event format in handleAuthMethodChange:', e);
      return;
    }
    
    // Schedule state changes for after current render cycle
    setTimeout(() => {
      setAuthMethod(newAuthMethod);
      
      // Don't clear credentials automatically - let user decide
      // This preserves user input when switching between auth methods
      
      // Clear validation errors for credentials
      const newErrors = {...validationErrors};
      delete newErrors.username;
      delete newErrors.password;
      delete newErrors.token;
      setValidationErrors(newErrors);
      
      // Focus the appropriate field based on the new auth method
      if (newAuthMethod === 'https' || newAuthMethod === 'basic') {
        if (usernameInputRef.current) {
          usernameInputRef.current.focus();
        }
      } else if (newAuthMethod === 'pat' || newAuthMethod === 'oauth') {
        if (tokenInputRef.current) {
          tokenInputRef.current.focus();
        }
      }
    }, 0);
  };
  
  // Toggle password visibility
  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  
  // Function to explicitly check for repository connection
  // This is not called on component mount to avoid unnecessary API calls
  const checkRepositoryConnection = async () => {
    if (!projectId) return;
    
    try {
      setIsSubmitting(true); // Use submitting state to show loading
      
      // Force a fresh check from the API, ignoring cached results
      const result = await getRepositoryInfo(projectId, true);
      if (result && result.url) {
        // Store connection data in sessionStorage to avoid future API calls
        sessionStorage.setItem(`git_repo_connection_${projectId}`, JSON.stringify(result));
        setConnectedRepo(result);
      } else {
        // If no connection found, explicitly set the not_found marker
        sessionStorage.setItem(`repo_check_${projectId}`, 'not_found');
        // Clear any stale connection data
        sessionStorage.removeItem(`git_repo_connection_${projectId}`);
        setConnectedRepo(null);
      }
    } catch (err) {
      console.log('No repository connected:', err.message);
      // Mark as checked but not found in sessionStorage
      sessionStorage.setItem(`repo_check_${projectId}`, 'not_found');
      // Clear any stale connection data
      sessionStorage.removeItem(`git_repo_connection_${projectId}`);
      setConnectedRepo(null);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Get base URL template based on provider
  const getUrlTemplate = () => {
    switch (provider) {
      case 'github':
        return 'https://github.com/username/repository.git';
      case 'gitlab':
        return 'https://gitlab.com/username/repository.git or https://your-gitlab-server.com/username/repository.git';
      case 'bitbucket':
        return 'https://bitbucket.org/username/repository.git';
      default:
        return 'https://git-server.com/username/repository.git';
    }
  };
  
  // Already connected repository view
  if (connectedRepo) {
    return (
      <Paper elevation={2} sx={{ 
        borderRadius: 2, 
        mb: 4, 
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        maxHeight: effectiveContainerHeight, // Use dynamic height
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto', // Allow scrolling for the entire component
        position: 'relative', // For absolute positioning inside
        // Custom scrollbar styling
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'rgba(0,0,0,0.05)',
          borderRadius: '8px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,0.2)',
          borderRadius: '8px',
          '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.3)',
          },
        },
      }}>
        {/* Fixed header section */}
        <Box sx={{ 
          p: 3,
          pb: 2,
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          position: 'sticky',
          top: 0,
          backgroundColor: 'white',
          zIndex: 10,
          width: '100%'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 500 }}>
              Connected Repository
            </Typography>
            
            <Button 
              variant="outlined" 
              color="error"
              onClick={handleDisconnect}
              disabled={loading}
              sx={{
                borderColor: loading ? 'rgba(211, 47, 47, 0.5)' : 'rgba(211, 47, 47, 0.5)',
                color: loading ? 'rgba(211, 47, 47, 0.5)' : '#d32f2f',
                '&:hover': {
                  borderColor: '#d32f2f',
                  backgroundColor: 'rgba(211, 47, 47, 0.04)'
                }
              }}
            >
              {loading ? <CircularProgress size={24} color="error" /> : 'Disconnect'}
            </Button>
          </Box>
        </Box>
        
        <Box sx={{ 
          p: 3,
          pt: 2,
          flexGrow: 1,
        }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500, fontSize: '0.875rem' }}>
            Repository URL
          </Typography>
          <Typography variant="body1" sx={{ fontSize: '1rem' }}>
            {connectedRepo.url}
          </Typography>
        </Box>
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500, fontSize: '0.875rem' }}>
            Provider
          </Typography>
          <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', fontSize: '1rem' }}>
            {repositoryProviders.find(p => p.value === connectedRepo.provider)?.icon}
            <Box component="span" sx={{ ml: 1 }}>
              {repositoryProviders.find(p => p.value === connectedRepo.provider)?.label || connectedRepo.provider}
            </Box>
          </Typography>
        </Box>
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500, fontSize: '0.875rem' }}>
            Branch
          </Typography>
          <Typography variant="body1" sx={{ fontSize: '1rem' }}>
            <Chip 
              label={connectedRepo.branch} 
              size="small" 
              variant="outlined" 
              sx={{ 
                backgroundColor: 'rgba(25, 118, 210, 0.08)', 
                borderColor: 'rgba(25, 118, 210, 0.2)',
                color: '#1976d2',
                fontWeight: 500
              }} 
            />
          </Typography>
        </Box>
        
        {connectedRepo.last_commit && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500, fontSize: '0.875rem' }}>
              Latest Commit
            </Typography>
            <Typography variant="body1" sx={{ wordBreak: 'break-all', fontSize: '0.875rem', fontFamily: 'monospace', backgroundColor: 'rgba(0,0,0,0.03)', p: 1, borderRadius: 1 }}>
              {connectedRepo.last_commit}
            </Typography>
          </Box>
        )}
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500, fontSize: '0.875rem' }}>
            File Count
          </Typography>
          <Typography variant="body1" sx={{ fontSize: '1rem' }}>
            {connectedRepo.file_count.toLocaleString()} files
          </Typography>
        </Box>
        
        </Box>
        
        <Alert 
          severity="success" 
          sx={{ 
            p: 3,
            py: 2,
            borderTop: '1px solid rgba(0, 0, 0, 0.08)',
            backgroundColor: 'rgba(46, 125, 50, 0.08)', 
            color: 'rgb(30, 70, 32)',
            '& .MuiAlert-icon': {
              color: '#2e7d32'
            },
            position: 'sticky',
            bottom: 0,
            zIndex: 10,
            borderRadius: 0,
            width: '100%',
            marginTop: 'auto' // Push to bottom when content is short
          }}
        >
          This repository is connected and ready to use with Claude Code
        </Alert>
      </Paper>
    );
  }
  
  // Connection form view
  return (
    <Paper elevation={2} sx={{ 
      borderRadius: 2, 
      mb: 4, 
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
      maxHeight: effectiveContainerHeight, // Use dynamic height
      overflow: 'auto', // Allow scrolling for the entire component
      position: 'relative', // For absolute positioning inside
      // Custom scrollbar styling
      '&::-webkit-scrollbar': {
        width: '8px',
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: 'rgba(0,0,0,0.05)',
        borderRadius: '8px',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'rgba(0,0,0,0.2)',
        borderRadius: '8px',
        '&:hover': {
          backgroundColor: 'rgba(0,0,0,0.3)',
        },
      },
    }}>
      {/* Fixed header section */}
      <Box sx={{ 
        p: 3,
        pb: 2,
        borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        position: 'sticky',
        top: 0,
        backgroundColor: 'white',
        zIndex: 10,
        width: '100%'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 500 }}>
            Connect Git Repository
          </Typography>
          
          <Button
            variant="text"
            size="small"
            onClick={checkRepositoryConnection}
            disabled={isSubmitting}
            sx={{ 
              color: '#1976d2',
              fontSize: '0.75rem',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.04)'
              }
            }}
          >
            {isSubmitting ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
            Check for existing connection
          </Button>
        </Box>
        
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mt: 2,
              backgroundColor: 'rgba(211, 47, 47, 0.08)', 
              color: 'rgb(95, 33, 32)',
              '& .MuiAlert-icon': {
                color: '#d32f2f'
              }
            }}
          >
            {error}
          </Alert>
        )}
      </Box>
      
      <Box 
        component="div" 
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          p: 3,
          pt: 2
        }}
      >
        <FormControl fullWidth margin="normal" variant="outlined">
          <Box sx={{ mb: 1, fontWeight: 500, fontSize: '0.875rem', color: 'rgba(0, 0, 0, 0.6)' }}>
            Repository Provider
          </Box>
          <Select
            id="provider"
            instanceId="provider-select"
            value={repositoryProviders.find(p => p.value === provider)}
            onChange={(selectedOption) => {
              const newProvider = selectedOption.value;
              
              // Schedule the state change for after the current render cycle
              setTimeout(() => {
                setProvider(newProvider);
                
                // For GitLab, default to PAT auth method which is more common
                if (newProvider === 'gitlab' && authMethod !== 'pat') {
                  setAuthMethod('pat');
                }
                
                // Don't automatically clear URL - let user decide to change it
                // This prevents losing user input when switching providers
                
                // Reset validation errors when changing provider
                setValidationErrors({});
                
                // Return focus to URL field after provider change
                if (repoUrlInputRef.current) {
                  repoUrlInputRef.current.focus();
                }
              }, 0);
            }}
            isDisabled={loading}
            options={repositoryProviders}
            formatOptionLabel={option => (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {option.icon}
                <Box component="span" sx={{ ml: 1 }}>
                  {option.label}
                </Box>
              </Box>
            )}
            styles={{
              control: (base) => ({
                ...base,
                minHeight: '56px',
                borderColor: 'rgba(0,0,0,0.15)',
                boxShadow: 'none',
                '&:hover': {
                  borderColor: 'rgba(0,0,0,0.25)'
                }
              }),
              menu: (base) => ({
                ...base,
                zIndex: 9999
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isSelected ? '#f0f7ff' : state.isFocused ? '#f5f5f5' : null,
                color: 'rgba(0, 0, 0, 0.87)',
                '&:active': {
                  backgroundColor: '#e3f2fd'
                }
              })
            }}
          />
        </FormControl>
        
        <FormControl fullWidth margin="normal" variant="outlined">
          <Box sx={{ mb: 1, fontWeight: 500, fontSize: '0.875rem', color: 'rgba(0, 0, 0, 0.6)' }}>
            Repository URL
          </Box>
          <TextField
            fullWidth
            id="repoUrl"
            value={repoUrl}
            onChange={(e) => {
              // Just update the state without validation
              setRepoUrl(e.target.value);
            }}
            onBlur={() => validateForm()}
            disabled={loading}
            error={!!validationErrors.repoUrl}
            placeholder={getUrlTemplate()}
            autoComplete="off" // Disable browser autocomplete
            inputRef={repoUrlInputRef} // Use ref to maintain focus
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-notchedOutline': { 
                borderColor: validationErrors.repoUrl ? 'red' : 'rgba(0,0,0,0.15)' 
              },
              '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: validationErrors.repoUrl ? 'red' : 'rgba(0,0,0,0.25)'
              }
            }}
          />
          {validationErrors.repoUrl ? (
            <FormHelperText error>{validationErrors.repoUrl}</FormHelperText>
          ) : (
            <FormHelperText>{`Example: ${getUrlTemplate()}`}</FormHelperText>
          )}
        </FormControl>
        
        <FormControl fullWidth margin="normal" variant="outlined">
          <Box sx={{ mb: 1, fontWeight: 500, fontSize: '0.875rem', color: 'rgba(0, 0, 0, 0.6)' }}>
            Authentication Method
          </Box>
          <Select
            id="auth-method"
            instanceId="auth-method-select"
            value={authMethods.find(m => m.value === authMethod)}
            onChange={(selectedOption) => {
              const newAuthMethod = selectedOption.value;
              const e = { target: { value: newAuthMethod } };
              handleAuthMethodChange(e);
            }}
            isDisabled={loading}
            options={authMethods}
            styles={{
              control: (base) => ({
                ...base,
                minHeight: '56px',
                borderColor: 'rgba(0,0,0,0.15)',
                boxShadow: 'none',
                '&:hover': {
                  borderColor: 'rgba(0,0,0,0.25)'
                }
              }),
              menu: (base) => ({
                ...base,
                zIndex: 9999
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isSelected ? '#f0f7ff' : state.isFocused ? '#f5f5f5' : null,
                color: 'rgba(0, 0, 0, 0.87)',
                '&:active': {
                  backgroundColor: '#e3f2fd'
                }
              })
            }}
          />
        </FormControl>
        
        {/* Credentials based on auth method */}
        {(authMethod === 'https' || authMethod === 'basic') && (
          <>
            <FormControl fullWidth margin="normal" variant="outlined">
              <Box sx={{ mb: 1, fontWeight: 500, fontSize: '0.875rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                Username
              </Box>
              <TextField
                fullWidth
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onBlur={() => validateForm()}
                disabled={loading}
                error={!!validationErrors.username}
                autoComplete="off"
                inputRef={usernameInputRef}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': { 
                    borderColor: validationErrors.username ? 'red' : 'rgba(0,0,0,0.15)' 
                  },
                  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: validationErrors.username ? 'red' : 'rgba(0,0,0,0.25)'
                  }
                }}
              />
              {validationErrors.username && (
                <FormHelperText error>{validationErrors.username}</FormHelperText>
              )}
            </FormControl>
            
            <FormControl fullWidth margin="normal" variant="outlined">
              <Box sx={{ mb: 1, fontWeight: 500, fontSize: '0.875rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                Password
              </Box>
              <TextField
                fullWidth
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => validateForm()}
                disabled={loading}
                error={!!validationErrors.password}
                autoComplete="new-password" // Prevent browser from autofilling
                inputRef={passwordInputRef}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': { 
                    borderColor: validationErrors.password ? 'red' : 'rgba(0,0,0,0.15)' 
                  },
                  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: validationErrors.password ? 'red' : 'rgba(0,0,0,0.25)'
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                        tabIndex={-1} // Prevent focus loss when clicking the icon
                        onMouseDown={(e) => e.preventDefault()} // Prevent focus loss
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              {validationErrors.password && (
                <FormHelperText error>{validationErrors.password}</FormHelperText>
              )}
            </FormControl>
          </>
        )}
        
        {(authMethod === 'pat' || authMethod === 'oauth') && (
          <FormControl fullWidth margin="normal" variant="outlined">
            <Box sx={{ mb: 1, fontWeight: 500, fontSize: '0.875rem', color: 'rgba(0, 0, 0, 0.6)' }}>
              {authMethod === 'pat' ? "Personal Access Token" : "OAuth Token"}
            </Box>
            <TextField
              fullWidth
              id="token"
              type={showPassword ? 'text' : 'password'}
              value={token}
              onChange={(e) => setToken(e.target.value)}
              onBlur={() => validateForm()}
              disabled={loading}
              error={!!validationErrors.token}
              autoComplete="off"
              inputRef={tokenInputRef}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-notchedOutline': { 
                  borderColor: validationErrors.token ? 'red' : 'rgba(0,0,0,0.15)' 
                },
                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: validationErrors.token ? 'red' : 'rgba(0,0,0,0.25)'
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle token visibility"
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                      tabIndex={-1} // Prevent focus loss when clicking the icon
                      onMouseDown={(e) => e.preventDefault()} // Prevent focus loss
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            {validationErrors.token ? (
              <FormHelperText error>{validationErrors.token}</FormHelperText>
            ) : (
              provider === 'gitlab' && (
                <FormHelperText>For GitLab, use a Project Access Token or Personal Access Token with "read_repository" scope</FormHelperText>
              )
            )}
          </FormControl>
        )}
        
        {/* Advanced options toggle */}
        <Box sx={{ mt: 3, mb: 1 }}>
          <FormControl fullWidth variant="outlined">
            <FormControlLabel
              control={
                <Switch
                  checked={showAdvanced}
                  onChange={(e) => setShowAdvanced(e.target.checked)}
                  disabled={loading}
                  color="primary"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#1976d2',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#1976d2',
                    },
                  }}
                />
              }
              label={
                <Typography sx={{ fontWeight: 500, fontSize: '0.875rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                  Show advanced options
                </Typography>
              }
            />
          </FormControl>
        </Box>
        
        {/* Advanced options */}
        {showAdvanced && (
          <Box sx={{ mt: 1, p: 2, bgcolor: 'rgba(0, 0, 0, 0.02)', borderRadius: 1, border: '1px solid rgba(0, 0, 0, 0.08)' }}>
            <FormControl fullWidth margin="normal" variant="outlined">
              <Box sx={{ mb: 1, fontWeight: 500, fontSize: '0.875rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                Branch
              </Box>
              <TextField
                id="branch"
                fullWidth
                value={branch}
                onChange={(e) => {
                  setBranch(e.target.value);
                  setTimeout(() => validateForm(), 0);
                }}
                onBlur={() => validateForm()}
                disabled={loading}
                error={!!validationErrors.branch}
                autoComplete="off"
                inputRef={branchInputRef}
                variant="outlined"
                placeholder="Enter branch name (e.g., main, master, develop)"
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': { 
                    borderColor: validationErrors.branch ? 'red' : 'rgba(0,0,0,0.15)' 
                  },
                  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: validationErrors.branch ? 'red' : 'rgba(0,0,0,0.25)'
                  }
                }}
              />
              {validationErrors.branch && (
                <FormHelperText error>{validationErrors.branch}</FormHelperText>
              )}
              {!validationErrors.branch && (
                <FormHelperText>The branch to clone (e.g., main, master, develop)</FormHelperText>
              )}
            </FormControl>
            
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
              <InfoIcon color="info" fontSize="small" sx={{ mr: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Advanced settings control which branch is used for code analysis
              </Typography>
            </Box>
          </Box>
        )}
        
      </Box>
      
      {/* Form actions - Position outside the scrollable area to always be visible */}
      <Box sx={{ 
        p: 3,
        pt: 2,
        display: 'flex', 
        justifyContent: 'flex-end',
        borderTop: '1px solid rgba(0, 0, 0, 0.08)',
        position: 'sticky',
        bottom: 0,
        backgroundColor: 'white', // Ensure the buttons have a solid background
        zIndex: 10, // Ensure buttons stay on top
        width: '100%',
        marginTop: 'auto' // Push to bottom when content is short
      }}>
          {onCancel && (
            <Button
              variant="outlined"
              onClick={onCancel}
              disabled={loading || isSubmitting}
              sx={{ 
                mr: 2, 
                borderColor: 'rgba(0, 0, 0, 0.23)',
                color: 'rgba(0, 0, 0, 0.87)',
                '&:hover': {
                  borderColor: 'rgba(0, 0, 0, 0.5)',
                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              Cancel
            </Button>
          )}
          
          <Button
            variant="contained"
            onClick={(e) => handleSubmit(e)}
            disabled={loading || isSubmitting}
            sx={{ 
              minWidth: '180px',
              backgroundColor: '#1976d2',
              '&:hover': {
                backgroundColor: '#1565c0'
              }
            }}
          >
            {loading || isSubmitting ? <CircularProgress size={24} /> : 'Connect Repository'}
          </Button>
        </Box>
    </Paper>
  );
};

export default GitRepositoryForm;